import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { AlertCircle } from "lucide-react";
import { TableInvoiceRow } from "../types/invoice-table.types";

export interface BookRemainderAsLossDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (lossComment: string) => Promise<void>;
	invoice: TableInvoiceRow | null | undefined;
	remainingAmount: number;
	loading?: boolean;
}

export const BookRemainderAsLossDialog: React.FC<BookRemainderAsLossDialogProps> = ({
	isOpen,
	onClose,
	onConfirm,
	invoice,
	remainingAmount,
	loading = false,
}) => {
	const invoiceNumber = invoice?.invoice_doc_number || "";
	const currency = invoice?.invoice_currency || "CHF";
	const [lossComment, setLossComment] = useState("");
	const [validationError, setValidationError] = useState("");

	const validateComment = (value: string): boolean => {
		const trimmedValue = value.trim();
		if (trimmedValue.length === 0) {
			setValidationError("Grund für Verlustbuchung ist erforderlich");
			return false;
		}
		setValidationError("");
		return true;
	};

	const handleCommentChange = (value: string) => {
		setLossComment(value);
		if (value.trim().length > 0) {
			validateComment(value);
		} else {
			setValidationError("");
		}
	};

	const handleConfirm = async () => {
		if (!validateComment(lossComment)) {
			return;
		}

		try {
			await onConfirm(lossComment.trim());
			handleClose();
		} catch (error) {
		}
	};

	const handleClose = () => {
		setLossComment("");
		setValidationError("");
		onClose();
	};

	const isCommentValid = lossComment.trim().length > 0;

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-orange-600">
						<AlertCircle className="h-5 w-5" />
						Restbetrag als Verlust buchen
					</DialogTitle>
					<DialogDescription>
						Sie sind dabei, den Restbetrag der Rechnung <strong>{invoiceNumber}</strong> als Verlust zu buchen.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="bg-orange-50 border border-orange-200 rounded-md p-3">
						<p className="text-sm font-medium text-orange-900">
							Verlustbetrag: {currency} {remainingAmount.toFixed(2)}
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="loss-comment" className="text-sm font-medium">
							Grund für Verlustbuchung <span className="text-red-600">*</span>
						</Label>
						<Textarea
							id="loss-comment"
							placeholder="Bitte geben Sie den Grund für die Verlustbuchung ein..."
							value={lossComment}
							onChange={(e) => handleCommentChange(e.target.value)}
							className={`resize-none ${validationError ? 'border-red-500 focus:border-red-500' : ''}`}
							rows={4}
							disabled={loading}
							data-testid="loss-comment-input"
						/>
						{validationError && (
							<p className="text-sm text-red-600" role="alert">
								{validationError}
							</p>
						)}
						<p className="text-xs text-gray-500">
							Grund ist erforderlich und wird in der Zahlungshistorie gespeichert
						</p>
					</div>
				</div>

				<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={loading}
						data-testid="cancel-loss-booking-button"
					>
						Abbrechen
					</Button>
					<Button
						variant="default"
						onClick={handleConfirm}
						disabled={!isCommentValid || loading}
						data-testid="confirm-loss-booking-button"
						className="bg-orange-600 hover:bg-orange-700"
					>
						{loading ? "Wird gebucht..." : "Als Verlust buchen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
